import '../../styles.scss';
import { DealSummaryActionsDropdownOptions, DealSummaryActionStatus } from '~/constants/deal-summary';
import MessagesButton from './MessagesButton';
import { Dropdown } from 'semantic-ui-react';
import { useEffect } from 'react';
import { usePageType, useProjectContext } from '~/hooks';
import useArchiveToast from '~/hooks/archive/useArchiveToast';
import { useQueryClient } from 'react-query';
import { getInvestorProjectDataroomAccessKey, useInvestorDataroomAccess } from '~/requests/dataroom';
import { useGrantInvestorDataroomAccess } from '~/mutations/dataroom/investorAccess/useGrantInvestorDataroomAccess';
import { useRevokeInvestorDataroomAccess } from '~/mutations/dataroom/investorAccess/useRevokeInvestorDataroomAccess';
import { DataroomAccess } from '~/types/dataroom';

interface ActionsDropdownMenuProps {
  investorId: string;
  awaitingBid: boolean;
  setModalType: (type: 'ACCEPT_BID' | 'DECLINE_BID' | null) => void;
}

type DropdownOption = {
  text: string;
  value: string;
}

const ActionsDropdownMenu = ({
  investorId,
  awaitingBid,
  setModalType,
}: ActionsDropdownMenuProps) => {
  const { project } = useProjectContext();
  const { showArchiveToast } = useArchiveToast();
  const queryClient = useQueryClient();
  const pageType = usePageType();

  const { data: investorDataroomAccess, status: getInvestorProjectDataroomAccessStatus, refetch: refetchInvestorDataroomAccess } = useInvestorDataroomAccess();
  const { mutate: grantInvestorProjectDataroomAccess, status: grantInvestorProjectDataroomAccessStatus } = useGrantInvestorDataroomAccess();
  const { mutate: revokeInvestorProjectDataroomAccess, status: revokeInvestorProjectDataroomAccessStatus } = useRevokeInvestorDataroomAccess();
  const loadingDataroomAccess = getInvestorProjectDataroomAccessStatus === 'loading' || grantInvestorProjectDataroomAccessStatus === 'loading' || revokeInvestorProjectDataroomAccessStatus === 'loading';

  const hasDataroomAccess = (investorId: string) => {
    return !!investorDataroomAccess?.investors?.find((data: DataroomAccess) => data.investorId === investorId);
  };

  const toggleDataroomAccess = async (investorId: string) => {
    if (showArchiveToast()) {
      return;
    }
    if (hasDataroomAccess(investorId)) {
      await revokeInvestorProjectDataroomAccess({ projectId: project.id, investorId } as any, { onSuccess: refetchInvestorDataroomAccess } as any);
    } else {
      await grantInvestorProjectDataroomAccess({ projectId: project.id, investorId } as any, { onSuccess: refetchInvestorDataroomAccess } as any);
    }
  };

  const handleClick = (option: DropdownOption) => {
    if (option.value === DealSummaryActionStatus.GRANT_DOCUMENT_ACCESS) {
      toggleDataroomAccess(investorId);
    } else if (option.value === DealSummaryActionStatus.END_CONVERSATION) {
      setModalType('DECLINE_BID');
    }
  };

  //This is required to keep dataroom access in sync with the project's bid summary
  useEffect(() => {
    if (pageType === 'project') {
      queryClient.invalidateQueries(getInvestorProjectDataroomAccessKey(pageType, project?.id));
    }
  }, [pageType, project?.id, queryClient]);


  return (
    <>
      {DealSummaryActionsDropdownOptions?.map((option: DropdownOption) => {
        let dropdownContent;
        if (option.value === DealSummaryActionStatus.VIEW_MESSAGES) {
          if (!awaitingBid) {
            dropdownContent = (
              <MessagesButton
                project={project}
                investorId={investorId}
              />
            );
          } else {
            return null;
          }
        } else if (option.value === DealSummaryActionStatus.GRANT_DOCUMENT_ACCESS) {
          dropdownContent = hasDataroomAccess(investorId)
            ? 'Revoke document access'
            : option.text;
        } else {
          dropdownContent = option.text;
        }

        return (
          <Dropdown.Item
            key={option.value}
            className='project-actions'
            style={{ ['--actions-color' as string]: option.value === DealSummaryActionStatus.END_CONVERSATION ? 'var(--color-red)' : 'var(--color-charcoal)' }}
            disabled={loadingDataroomAccess}
            onClick={() => handleClick(option)}
          >
            {dropdownContent}
          </Dropdown.Item>
        );
      })}
    </>
  );
};

export default ActionsDropdownMenu;